import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import {
  currencyOptions,
  Currency,
  currencySymbols,
  getLocationPlans,
  customOffsetsCosts,
  customTreesCosts,
  edenPriceMatchTreesCosts,
  User,
  Location,
  CampaignType,
  campaigns,
  toCurrency,
  toPlural,
  pluralise,
  CreateDonationArgs,
} from "@ecologi/shared";
import { Tabs } from "../../DeprecatedTabs";
import { Button } from "../../Button";
import { Checkbox } from "../../Checkbox";
import { TextField } from "../../TextField";
import { Select } from "../../Select";
import { CustomDonationForm } from "./CustomDonationForm";
import { DonateConfirmationModal } from "./DonateConfirmationModal";
import {
  getDonationImpactMapper,
  donationImpactMapperLapland,
  DonationType,
} from "../consts/donation-options";
import { donationsTabs } from "../consts/donation-tabs";
import { getDonationOptions } from "../utils/get-donation-options";
import { OptionList } from "../../OptionList";
import styles from "../styles/DonationModal.module.scss";
import { DialogModal } from "../../DialogModal";

export const donationTypeMap: { [key: string]: number } = {
  default: 0,
  true: 0, // legacy
  both: 0,
  trees: 1,
  custom: 2,
};

const TextContainer = styled.div`
  > *:not(:first-child) {
    margin-top: 16px;
  }
`;

type DonateModalProps = {
  campaign?: CampaignType | null;
  close: () => void;
  initialDonationTab: typeof donationTypeMap.default;
  isOpen: boolean;
  isOwnProfile: boolean;
  isWaitingForPaymentProcessor: boolean;
  location: Location;
  profile: User; // current profile donating to
  createDonationCharge: (args: CreateDonationArgs) => Promise<void>;
  redirectToCheckout: (args: CreateDonationArgs) => Promise<void>;
  referringService: string;
  user: User | null;
};

export const DonateModal: React.FC<DonateModalProps> = ({
  campaign,
  close,
  createDonationCharge,
  initialDonationTab,
  isOpen,
  isOwnProfile,
  isWaitingForPaymentProcessor,
  location,
  profile,
  redirectToCheckout,
  referringService,
  user,
}) => {
  // If the logged in user has billing enabled or they're a pay-by-invoice user,
  // default to billing their saved card/invoice
  const [isUseSavedPaymentMethodChecked, setUseSavedPaymentMethod] =
    useState<boolean>(user?.isBillingEnabled || false);
  const [isConfirmationModalOpen, setConfirmationModal] =
    useState<boolean>(false);

  const [isChargingCard, setChargingCardState] = useState(false);

  const isTreeMode = profile.isTreeMode;

  const [selectedCurrency, setSelectedCurrency] = useState(
    getLocationPlans(location).currencyCode
  );

  useEffect(() => {
    // Can be in a state where modal opens before location can be determined
    setSelectedCurrency(getLocationPlans(location).currencyCode);
  }, [location]);

  const [selectedDonation, setSelectedDonation] = useState("");

  const [customNumberOfTrees, setCustomNumberOfTrees] = useState(0);
  const [customNumberOfOffsets, setCustomNumberOfOffsets] = useState(0);
  const treesCosts =
    isOwnProfile && user?.isEdenPriceMatchEnabled
      ? edenPriceMatchTreesCosts
      : customTreesCosts;

  const customTotal =
    customNumberOfTrees * treesCosts[selectedCurrency] +
    customNumberOfOffsets * customOffsetsCosts[selectedCurrency];

  const customDonationProps = {
    customTotal,
    currencySymbol: currencySymbols[selectedCurrency],
    customNumberOfOffsets,
    customNumberOfTrees,
    setCustomNumberOfOffsets,
    setCustomNumberOfTrees,
  };

  const [displayName, setDisplayName] = useState("");
  const name =
    profile.isFamilyPlan || profile.isBusinessPlan || profile.isSubscriptionless
      ? profile.groupName
      : profile.firstName;
  const namePlural = toPlural(name || "");

  const tabs = isTreeMode ? donationsTabs.treeMode : donationsTabs.normal;
  const [selectedTab, setSelectedTab] = useState(
    tabs[initialDonationTab || 0].id
  );

  const isBoth = selectedTab === "BOTH";
  const isTrees = selectedTab === "TREES";
  const isOffsets = false; //selectedTab === "OFFSETS";
  const isCustom = selectedTab === "CUSTOM";

  // bespoke: Forest of love has different copy
  const isForestOfLove = campaign === campaigns.forestOfLove.id;

  // bespoke: Lapland have slightly different donation types
  const isLapland = campaign === campaigns.lapland.id;
  const donationMapper = isLapland
    ? donationImpactMapperLapland
    : getDonationImpactMapper(isOwnProfile ? user : profile, isOwnProfile);

  const { numberOfTrees, numberOfTonnes } = selectedDonation
    ? (donationMapper[selectedTab]![selectedCurrency][selectedDonation] as {
        numberOfTrees: number;
        numberOfTonnes: number;
      })
    : {
        numberOfTrees: customNumberOfTrees,
        numberOfTonnes: customNumberOfOffsets,
      };

  const totalCost = Math.round(
    isCustom ? customTotal * 100 : parseInt(selectedDonation)
  );

  let title = isOwnProfile
    ? "Increase your impact with a one-time purchase"
    : `Help ${name} increase their impact with a gift`;
  let text = `Your payment will fund ${
    isBoth || isTrees ? "tree-planting " : ""
  } ${isBoth ? "and " : ""} ${
    isBoth || isOffsets ? "carbon avoidance " : ""
  } projects. The impact will be shown on ${
    isOwnProfile ? "your" : namePlural
  } Ecologi profile.`;

  if (profile.username === "voteleaves") {
    title = "Plant real trees in the #VoteLeaves forest";
    text =
      "Your payment will fund real trees being planted responsibly in Madagascar. Your contributions will be displayed in the #VoteLeaves Ecologi forest.";
  }

  if (isCustom) {
    text = `Add a custom amount of trees and carbon avoidance. The impact will be shown on ${
      isOwnProfile ? "your" : namePlural
    } Ecologi profile.`;
  }

  if (isForestOfLove) {
    title = "Plant real trees in the Forest of Love";
    text =
      "Your payment will fund real trees planted responsibly in Madagascar. The new trees will be shown in the Forest of Love for you and your valentine to see.";
  }

  let isCustomValid = customNumberOfTrees >= 25 || customNumberOfOffsets >= 1;
  if (customNumberOfTrees > 0 && customNumberOfTrees < 25) {
    isCustomValid = false;
  }

  const checkoutData = {
    numberOfTonnes: numberOfTonnes || 0,
    numberOfTrees: numberOfTrees || 0,
    displayName,
    campaign,
    referringService: referringService || null,
    userId: profile.userId,
    isEdenPriceMatched:
      (user?.isEdenPriceMatchEnabled && isOwnProfile) || false,
  };

  const maskedCardNo = user?.maskedCardNumber.replace("************", "");
  const totalBillingCost = toCurrency(totalCost / 100);
  const isDonatingTrees = numberOfTrees > 0;
  const isDonatingBoth = isDonatingTrees && numberOfTonnes > 0;

  let confirmationDescription = `You have selected ${pluralise(
    numberOfTonnes || 0,
    "tonne"
  )} of carbon avoidance.`;
  if (isDonatingBoth) {
    confirmationDescription = `You have selected ${pluralise(
      numberOfTrees,
      "tree"
    )} and ${pluralise(numberOfTonnes, "tonne")} of carbon avoidance.`;
  } else if (isDonatingTrees) {
    confirmationDescription = `You have selected ${numberOfTrees} trees.`;
  }

  confirmationDescription = `${confirmationDescription} The total billing amount will be <strong>${currencySymbols[selectedCurrency]}${totalBillingCost}</strong>.`;

  let checkboxLabelText = `Use the card details we have saved${
    maskedCardNo ? ` (ending in ${maskedCardNo})` : ""
  }`;

  if (user?.isPayByInvoiceCustomer) {
    confirmationDescription = `${confirmationDescription} This will be added to your next invoice. Do you wish to continue?`;
    checkboxLabelText = "Add this payment to your next invoice";
  } else {
    confirmationDescription = `${confirmationDescription} Your saved card ${
      user?.maskedCardNumber ? `(ending in ${maskedCardNo})` : ""
    } will be directly charged. Do you wish to continue?`;
  }

  const TabsWrapper = styled.div`
    margin-bottom: 10px;
  `;

  return (
    <>
      <DialogModal
        isOpen={isOpen && !isConfirmationModalOpen}
        onClose={() => {
          setSelectedDonation("");
          close();
        }}
        scrollable
        title={title}
      >
        <>
          {user?.isBillingEnabled && (
            <div
              className={styles.cardDetailsCheckbox}
              data-checked={isUseSavedPaymentMethodChecked}
            >
              <Checkbox
                label="Use saved payment details"
                isSelected={isUseSavedPaymentMethodChecked}
                onChange={setUseSavedPaymentMethod}
              >
                {checkboxLabelText}
              </Checkbox>
            </div>
          )}
          {!isTreeMode && (
            <TabsWrapper id="DonationModalTabs">
              <Tabs
                tabs={
                  isTreeMode ? donationsTabs.treeMode : donationsTabs.normal
                }
                active={selectedTab}
                onSelectTab={(_, id) => {
                  setSelectedDonation("");
                  setSelectedTab((id as DonationType) || "TREES");
                }}
                smallText
                noBreakMobile
              />
            </TabsWrapper>
          )}
          <TextContainer>
            <p>{text}</p>
            <Select
              name="currency"
              label="Currency"
              // labelInline
              items={currencyOptions}
              onChange={(event) => {
                setSelectedDonation("");
                setSelectedCurrency(event.target.value as Currency);
              }}
              value={selectedCurrency}
            />
            {isCustom ? (
              <CustomDonationForm {...customDonationProps} />
            ) : (
              <>
                <OptionList
                  title="Select a bundle"
                  options={getDonationOptions(
                    selectedTab,
                    selectedCurrency,
                    campaign,
                    isOwnProfile ? user : profile,
                    isOwnProfile
                  )}
                  onSelectOption={(option) => {
                    setSelectedDonation(option);
                  }}
                  selectedOption={selectedDonation}
                />
              </>
            )}

            <TextField
              name="displayName"
              helpText={
                "This name will be shown when someone views a tile on this profile. You can use your name or leave it blank to remain anonymous."
              }
              label={
                isForestOfLove ? "Message to your valentine" : "Display name"
              }
              maxLength={30}
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
              placeholder={
                isForestOfLove
                  ? "E.g. I love you Lucy! - Love Elliot x"
                  : "E.g. Lucy Green"
              }
              showCharacterCount
              value={displayName}
            />
            {(isBoth || isCustom) && <TermsBoth />}
            {isTrees && <TermsTrees />}
            {isOffsets && <TermsOffsets />}
          </TextContainer>

          <div style={{ flex: "1 0 auto", marginTop: "16px" }}>
            <Button
              disabled={
                (isCustom ? !isCustomValid : !selectedDonation) ||
                isChargingCard ||
                isWaitingForPaymentProcessor
              }
              primary
              onClick={() => {
                if (isUseSavedPaymentMethodChecked) {
                  setConfirmationModal(true);
                } else {
                  redirectToCheckout({
                    userId: checkoutData.userId,
                    currency: selectedCurrency,
                    campaign: checkoutData.campaign || null,
                    displayName: checkoutData.displayName,
                    numberOfTrees: checkoutData.numberOfTrees,
                    numberOfTonnes: checkoutData.numberOfTonnes,
                    referringService: checkoutData.referringService,
                    isEdenPriceMatched: checkoutData.isEdenPriceMatched,
                  });
                }
              }}
              loading={isWaitingForPaymentProcessor}
              text={
                isUseSavedPaymentMethodChecked
                  ? user?.isPayByInvoiceCustomer
                    ? "Confirm purchase"
                    : "Make secure payment"
                  : "Continue to secure checkout"
              }
            />
          </div>
        </>
      </DialogModal>

      {isConfirmationModalOpen && (
        <DonateConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isChargingCard}
          primaryBtnText={user?.isPayByInvoiceCustomer ? "Confirm" : undefined}
          onClose={() => setConfirmationModal(false)}
          onPrimaryAction={async () => {
            try {
              setChargingCardState(true);
              await createDonationCharge({
                userId: checkoutData.userId,
                currency: selectedCurrency,
                campaign: checkoutData.campaign || null,
                displayName: checkoutData.displayName,
                numberOfTrees: checkoutData.numberOfTrees,
                numberOfTonnes: checkoutData.numberOfTonnes,
                referringService: checkoutData.referringService,
                isEdenPriceMatched: checkoutData.isEdenPriceMatched,
              });
              setChargingCardState(false);
              setSelectedDonation("");
              setConfirmationModal(false);
            } catch (error) {
              toast.error(error.message);
              setChargingCardState(false);
            }
          }}
          content={confirmationDescription}
        />
      )}
    </>
  );
};

const TermsBoth = () => (
  <p className={styles.terms}>
    With this payment you&rsquo;ll be funding an amount of trees to be planted
    by our trusted reforestation partners, and also an amount of carbon
    avoidance in the form of credits adhering to one of the leading carbon
    standards. All of our projects contribute impacts toward the UN Sustainable
    Development Goals.
  </p>
);

const TermsTrees = () => (
  <p className={styles.terms}>
    With this payment you&rsquo;ll be funding an amount of trees to be planted
    by our trusted reforestation partners. All of our partners maintain
    exceptional and rigorous standards to ensure the maximum possible climate
    and ecological impact, as well as contributing to the UN Sustainable
    Development Goals.
  </p>
);

const TermsOffsets = () => (
  <p className={styles.terms}>
    With this payment you’ll be buying an amount of carbon offsets from{" "}
    <a
      href="https://www.goldstandard.org/"
      rel="noopener noreferrer"
      style={{ color: "#008B6A" }}
      target="_blank"
      title="View website (new tab)"
    >
      Gold Standard
    </a>
    . They have exceptional and rigorous standards to ensure they fulfil the
    necessary climate impact and Sustainable Development Goals.
  </p>
);
